import React, { useMemo, useRef, useState } from 'react';
import { Box, Typography, IconButton, Button, Stack, Chip } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { IWorkProfile } from '../../types/global/helper';
import { useThemeContext } from '../../hook/useThemeManager';
import { theme } from '../../constants/theme';
import { FONT_ENUM } from '../../types/global/fonts.types';
import PhotoCamera from '@mui/icons-material/PhotoCamera';
import EditOutlined from '@mui/icons-material/EditOutlined';
import LocationOn from '@mui/icons-material/LocationOn';
import Star from '@mui/icons-material/Star';
import { RectangularImageCropperModal } from '../ImageCropper/RectangularImageCropperModal';
import { StarRating } from '../EsVideoVotingModal/EsRatingStars';
import { formatWorkProfileLocationLine } from '../../utils/AppUtils';

type HeroCtaVariant = 'paired' | 'editSolo';

/** `paired`: Save + Cancel same width. `editSolo`: Edit only — smaller on xs, paired sizing from sm up. */
function useHeroCtaButtonSx(themeStyle: keyof typeof theme, borderRadius: number | string, variant: HeroCtaVariant) {
    return useMemo(() => {
        const shared = {
            borderRadius,
            boxSizing: 'border-box' as const,
            justifyContent: 'center' as const,
            bgcolor: theme[themeStyle].highlight,
            color: theme[themeStyle].black,
            textTransform: 'none' as const,
            fontFamily: FONT_ENUM.MAIN_BOLD,
            fontWeight: 700,
            py: { xs: 0.65, sm: 1.25 },
            fontSize: { xs: '0.7rem', sm: '0.875rem' },
            '&:hover': {
                bgcolor: theme[themeStyle].primary_orange,
                color: theme[themeStyle].black,
            },
        };
        if (variant === 'paired') {
            return {
                ...shared,
                width: { xs: 112, sm: 152 },
                minWidth: { xs: 112, sm: 152 },
                maxWidth: { xs: 112, sm: 152 },
                px: { xs: 0.75, sm: 2 },
            };
        }
        return {
            ...shared,
            width: { xs: 'auto', sm: 152 },
            minWidth: { xs: 76, sm: 152 },
            maxWidth: { xs: 'none', sm: 152 },
            px: { xs: 2, sm: 2 },
        };
    }, [themeStyle, borderRadius, variant]);
}

function splitStageNameForHero(name: string): { first: string; last: string } {
    const t = name.trim();
    if (!t) return { first: '', last: '' };
    const parts = t.split(/\s+/);
    if (parts.length === 1) return { first: '', last: parts[0] };
    return { first: parts.slice(0, -1).join(' '), last: parts[parts.length - 1] };
}

interface WorkProfileHeaderProps {
    profile: IWorkProfile;
    editMode?: boolean;
    onImageChange?: (base64: string, file: File) => void;
    hideRating?: boolean;
    showHeroEditButton?: boolean;
    heroEditLabel?: string;
    onHeroEditClick?: VoidFunction;
    showHeroSaveButton?: boolean;
    heroSaveLabel?: string;
    /** When true, hero Save is non-interactive and muted (e.g. no media changes yet). */
    heroSaveDisabled?: boolean;
    onHeroSaveClick?: VoidFunction;
}

const WorkProfileHeader: React.FC<WorkProfileHeaderProps> = ({
    profile,
    editMode = false,
    onImageChange,
    hideRating = false,
    showHeroEditButton = false,
    heroEditLabel = 'Edit',
    onHeroEditClick,
    showHeroSaveButton = false,
    heroSaveLabel = 'Save',
    heroSaveDisabled = false,
    onHeroSaveClick,
}) => {
    const { themeStyle } = useThemeContext();
    const heroSavePillSx = useHeroCtaButtonSx(themeStyle, 999, 'paired');
    const heroEditPillSx = useHeroCtaButtonSx(themeStyle, 999, showHeroSaveButton ? 'paired' : 'editSolo');
    const heroSaveRoundedSx = useHeroCtaButtonSx(themeStyle, 3, 'paired');
    const heroEditRoundedSx = useHeroCtaButtonSx(themeStyle, 3, showHeroSaveButton ? 'paired' : 'editSolo');
    const heroSaveDisabledSx = {
        '&.Mui-disabled': {
            bgcolor: `${theme[themeStyle].darkGray60} !important`,
            color: 'rgba(255,255,255,0.48) !important',
        },
    };
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [src, setSrc] = useState<string>('');
    const [pendingImage, setPendingImage] = useState<string | null>(null);
    const [pendingFile, setPendingFile] = useState<File | null>(null);
    const displayName = profile.stage_name
        ? profile.stage_name
        : profile.user?.firstName
        ? profile.user?.firstName + ''
        : profile.created_by?.firstName
        ? profile.created_by?.firstName
        : '';

    const isArtistProfile = profile.profile_type === 'artist';
    const isPerformerProfile = profile.profile_type === 'performer';
    const isTechnicalProfile = profile.profile_type === 'technical';
    const isContentCreatorProfile = profile.profile_type === 'content_creator';
    const isVoiceoverProfile = profile.profile_type === 'voiceover';
    /** Large hero identity (avatar, name, @handle) shared with artist layout. */
    const usesArtistStyleHero = isArtistProfile || isTechnicalProfile || isContentCreatorProfile || isVoiceoverProfile;
    const locationLine = formatWorkProfileLocationLine(profile.location);

    // Hero banner: feature image, else reuse profile picture (same sources as the avatar)
    const displayImage =
        pendingImage ||
        profile.feature_picture ||
        profile.profile_picture ||
        profile.user?.profile_picture ||
        profile.photos?.[0];

    // Upload/crop logic
    const handleUploadClick = () => {
        if (fileInputRef.current) fileInputRef.current.click();
    };
    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;
        if (files && files.length > 0) {
            const file = files[0];
            setSrc(URL.createObjectURL(file));
            setModalOpen(true);
        }
    };
    const handleCropSave = (base64: string, file: File) => {
        setModalOpen(false);
        setSrc('');
        setPendingImage(base64);
        setPendingFile(file);
    };
    const handleCropCancel = () => {
        setModalOpen(false);
        setSrc('');
    };
    const handlePendingCancel = () => {
        setPendingImage(null);
        setPendingFile(null);
    };
    const handlePendingSave = () => {
        if (pendingImage && pendingFile && onImageChange) {
            onImageChange(URL.createObjectURL(pendingFile), pendingFile);
        }
        setPendingImage(null);
        setPendingFile(null);
    };

    // Responsive
    const isMobile = typeof window !== 'undefined' && window.innerWidth < 600;
    const isBelowDesktop = typeof window !== 'undefined' && window.innerWidth < 1200;
    const mobileBaseHeroHeight = usesArtistStyleHero ? 360 : 200;
    const mobileEditStackExtraHeight = showHeroSaveButton ? 72 : 0;
    const imageHeight = isMobile ? mobileBaseHeroHeight + mobileEditStackExtraHeight : 520;
    const numericRating = Number(profile.rating);
    const number_of_reviews = Number(profile.number_of_reviews ?? 0);
    const hasReviewInfo = Number.isFinite(numericRating) && number_of_reviews > 0;
    const rating = hasReviewInfo ? numericRating.toFixed(1) : '';
    const hasHeroActions = showHeroEditButton || showHeroSaveButton;
    const mobileHeroActionInset = isBelowDesktop ? (showHeroSaveButton ? 176 : 96) : 0;

    /** Same stacking idea as `CoverImageRenderer` immersive hero: first layer = top, url = bottom. */
    const pageBase = theme[themeStyle].form_gray;
    const heroBackgroundImage = displayImage
        ? [
              `linear-gradient(to bottom, transparent 0%, transparent calc(100% - 56px), ${pageBase} 100%)`,
              ...(usesArtistStyleHero
                  ? [
                        'linear-gradient(to right, rgba(14,14,14,0.88) 0%, rgba(14,14,14,0.4) 45%, transparent 72%)',
                        'linear-gradient(to top, rgba(14,14,14,0.75) 0%, rgba(14,14,14,0.25) 50%, transparent 78%)',
                    ]
                  : []),
              ...(isPerformerProfile
                  ? [
                        `linear-gradient(to top, ${pageBase} 0%, ${alpha(pageBase, 0.4)} 38%, transparent 68%)`,
                        'linear-gradient(to right, rgba(14,14,14,0.82) 0%, rgba(14,14,14,0.22) 48%, transparent 72%)',
                    ]
                  : []),
              ...(!isPerformerProfile && !usesArtistStyleHero
                  ? [
                        'linear-gradient(to top, rgba(14, 14, 14, 0.95) 0%, rgba(14, 14, 14, 0.5) 42%, transparent 72%)',
                        isMobile
                            ? 'linear-gradient(to top, rgba(0, 0, 0, 0.55), rgba(0, 0, 0, 0.2))'
                            : 'linear-gradient(to right, rgba(0, 0, 0, 0.55), rgba(0, 0, 0, 0.2))',
                        'radial-gradient(circle at bottom left, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.25) 40%, rgba(0,0,0,0) 75%)',
                    ]
                  : []),
              `url(${displayImage})`,
          ].join(', ')
        : undefined;

    const performerTypeLabel =
        profile.profile_type === 'performer'
            ? 'Performer'
            : profile.profile_type
            ? profile.profile_type.replace(/_/g, ' ')
            : '';
    const heroNameParts = splitStageNameForHero(displayName);
    const profileHandle =
        profile.user?.userName ||
        profile.created_by?.userName ||
        (displayName || '').toString().replace(/\s+/g, '_').toLowerCase();

    return (
        <Box sx={{ width: '100%', mt: 0 }}>
            <Box
                sx={{
                    width: '100%',
                    position: 'relative',
                    /* Must be flush with `form_gray` block below or the gap reads as a hard seam. */
                    mb: 0,
                    p: 0,
                    borderRadius: 0,
                }}>
                {/* Cover Image — full-bleed top, flush to modal/drawer width */}
                <Box
                    sx={{
                        width: '100%',
                        height: imageHeight,
                        borderRadius: 0,
                        overflow: 'hidden',
                        backgroundColor: theme[themeStyle].secondaryBg,
                        backgroundImage: heroBackgroundImage,
                        backgroundSize: displayImage ? 'cover' : undefined,
                        backgroundPosition: 'center',
                        backgroundRepeat: 'no-repeat',
                        position: 'relative',
                    }}>
                    {(usesArtistStyleHero || isPerformerProfile) && displayImage && (
                        <Box
                            sx={{
                                position: 'absolute',
                                inset: 0,
                                zIndex: 0,
                                bgcolor: 'rgba(0,0,0,0.28)',
                                pointerEvents: 'none',
                            }}
                        />
                    )}
                    {/* Frosted strip: blurs busy artwork into the page background (audition uses gradients only; this adds visible blur). */}
                    {displayImage && (
                        <Box
                            sx={{
                                position: 'absolute',
                                left: 0,
                                right: 0,
                                bottom: 0,
                                height: { xs: isPerformerProfile ? 88 : 72, sm: isPerformerProfile ? 112 : 96 },
                                zIndex: 1,
                                pointerEvents: 'none',
                                backdropFilter: 'blur(22px)',
                                WebkitBackdropFilter: 'blur(22px)',
                                maskImage:
                                    'linear-gradient(to bottom, transparent 0%, rgba(0,0,0,0.85) 42%, rgba(0,0,0,1) 100%)',
                                WebkitMaskImage:
                                    'linear-gradient(to bottom, transparent 0%, rgba(0,0,0,0.85) 42%, rgba(0,0,0,1) 100%)',
                                background: `linear-gradient(to top, ${alpha(pageBase, 0.9)} 0%, ${alpha(
                                    pageBase,
                                    0.25,
                                )} 52%, transparent 100%)`,
                            }}
                        />
                    )}

                    {/* Info and CTA inside image */}
                    {usesArtistStyleHero ? (
                        <Stack
                            direction="row"
                            alignItems="flex-end"
                            justifyContent="space-between"
                            spacing={{ xs: 1.25, sm: 2, md: 2.5 }}
                            sx={{
                                position: 'absolute',
                                left: 0,
                                right: 0,
                                bottom: 0,
                                zIndex: 2,
                                color: theme[themeStyle].true_white,
                                px: { xs: 2, sm: 3 },
                                pb: { xs: 1.5 + mobileHeroActionInset / 16, md: 3.5 },
                                pt: { xs: 4, sm: 8 },
                                maxWidth: 'min(1280px, 100%)',
                                mx: 'auto',
                                width: '100%',
                                boxSizing: 'border-box',
                            }}>
                            <Stack
                                direction="row"
                                spacing={{ xs: 1.1, sm: 2.5, md: 3 }}
                                alignItems={{ xs: 'flex-end', md: 'center' }}
                                sx={{ flex: 1, minWidth: 0, mt: 0 }}>
                                <Box
                                    sx={{
                                        width: { xs: 64, sm: 140, md: 160 },
                                        height: { xs: 64, sm: 140, md: 160 },
                                        borderRadius: { xs: '12px', sm: '16px' },
                                        overflow: 'hidden',
                                        borderWidth: { xs: 2, sm: 4 },
                                        borderStyle: 'solid',
                                        borderColor: theme[themeStyle].highlight,
                                        backgroundColor: '#111',
                                        flexShrink: 0,
                                        boxShadow: '0 20px 50px rgba(0,0,0,0.45)',
                                    }}>
                                    <img
                                        src={
                                            profile.profile_picture ||
                                            profile.user?.profile_picture ||
                                            displayImage ||
                                            ''
                                        }
                                        alt={displayName || 'Profile'}
                                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                    />
                                </Box>
                                <Stack spacing={{ xs: 0.5, sm: 1.25 }} sx={{ flex: 1, minWidth: 0, pb: { md: 0.5 } }}>
                                    <Stack
                                        direction="row"
                                        alignItems="center"
                                        flexWrap="wrap"
                                        gap={{ xs: 0.75, sm: 1.25 }}>
                                        <Chip
                                            label={
                                                isArtistProfile
                                                    ? 'Pro Artist'
                                                    : isContentCreatorProfile
                                                    ? 'Content Creator'
                                                    : isVoiceoverProfile
                                                    ? 'Voiceover'
                                                    : 'Technical'
                                            }
                                            size="small"
                                            sx={{
                                                height: { xs: 22, sm: 26 },
                                                fontSize: { xs: 8, sm: 10 },
                                                fontWeight: 700,
                                                letterSpacing: { xs: '0.08em', sm: '0.12em' },
                                                textTransform: 'uppercase',
                                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                                bgcolor: 'rgba(250,175,63,0.18)',
                                                color: theme[themeStyle].highlight,
                                                border: 'none',
                                                '& .MuiChip-label': { px: { xs: 0.75, sm: 1.25 } },
                                            }}
                                        />
                                        {!hideRating &&
                                            (hasReviewInfo ? (
                                                <Stack direction="row" alignItems="center" spacing={0.5}>
                                                    <Star
                                                        sx={{
                                                            fontSize: { xs: 14, sm: 18 },
                                                            color: theme[themeStyle].highlight,
                                                        }}
                                                    />
                                                    <Typography
                                                        sx={{
                                                            fontWeight: 700,
                                                            fontSize: { xs: 11, sm: 14 },
                                                            fontFamily: FONT_ENUM.MAIN_BOLD,
                                                        }}>
                                                        {rating}
                                                    </Typography>
                                                    <Typography
                                                        sx={{
                                                            fontSize: { xs: 10, sm: 12 },
                                                            color: 'rgba(255,255,255,0.45)',
                                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                        }}>
                                                        ({number_of_reviews} reviews)
                                                    </Typography>
                                                </Stack>
                                            ) : (
                                                <Typography
                                                    sx={{
                                                        fontSize: { xs: 10, sm: 12 },
                                                        color: 'rgba(255,255,255,0.45)',
                                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                    }}>
                                                    No reviews yet
                                                </Typography>
                                            ))}
                                    </Stack>
                                    <Typography
                                        sx={{
                                            fontWeight: 900,
                                            fontFamily: FONT_ENUM.BOLD_HEADER,
                                            fontSize: { xs: '1.2rem', sm: '2.4rem', md: '3.25rem', lg: '3.75rem' },
                                            lineHeight: 1.02,
                                            letterSpacing: '-0.03em',
                                            textTransform: 'uppercase',
                                            whiteSpace: { xs: 'nowrap', sm: 'normal' },
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                        }}>
                                        {displayName}
                                    </Typography>
                                    <Stack
                                        direction="row"
                                        alignItems="center"
                                        flexWrap="wrap"
                                        gap={{ xs: 0.75, sm: 1.25 }}
                                        sx={{ mt: { xs: 0, sm: 0.5 } }}>
                                        {profile.title ? (
                                            <Typography
                                                sx={{
                                                    color: theme[themeStyle].highlight,
                                                    fontWeight: 500,
                                                    fontFamily: FONT_ENUM.BOLD_HEADER,
                                                    fontSize: { xs: '0.85rem', sm: '1.35rem' },
                                                }}>
                                                {profile.title}
                                            </Typography>
                                        ) : null}
                                        {profile.title && locationLine ? (
                                            <Box
                                                sx={{
                                                    width: { xs: 5, sm: 8 },
                                                    height: { xs: 5, sm: 8 },
                                                    borderRadius: '50%',
                                                    bgcolor: 'rgba(255,255,255,0.22)',
                                                    flexShrink: 0,
                                                }}
                                            />
                                        ) : null}
                                        {locationLine ? (
                                            <Stack direction="row" alignItems="center" spacing={0.5}>
                                                <LocationOn
                                                    sx={{
                                                        fontSize: { xs: 14, sm: 20 },
                                                        color: 'rgba(255,255,255,0.4)',
                                                    }}
                                                />
                                                <Typography
                                                    sx={{
                                                        color: 'rgba(255,255,255,0.65)',
                                                        fontSize: { xs: '0.75rem', sm: '1.1rem' },
                                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                    }}>
                                                    {locationLine}
                                                </Typography>
                                            </Stack>
                                        ) : null}
                                    </Stack>
                                    <Typography
                                        sx={{
                                            color: theme[themeStyle].highlight,
                                            fontWeight: 600,
                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                            fontSize: { xs: 10, sm: 12 },
                                            opacity: 0.95,
                                            mt: { xs: 0, sm: 0.25 },
                                        }}>
                                        @{profileHandle}
                                    </Typography>
                                </Stack>
                            </Stack>
                            {(showHeroEditButton || showHeroSaveButton) && (
                                <Stack
                                    direction={{ xs: 'column', md: 'row' }}
                                    spacing={{ xs: 0.75, sm: 1.25 }}
                                    alignItems="flex-end"
                                    sx={{ flexShrink: 0, display: { xs: 'none', lg: 'flex' } }}>
                                    {showHeroSaveButton && onHeroSaveClick && (
                                        <Button
                                            disabled={heroSaveDisabled}
                                            onClick={onHeroSaveClick}
                                            sx={{ ...heroSavePillSx, ...heroSaveDisabledSx }}>
                                            {heroSaveLabel}
                                        </Button>
                                    )}
                                    {showHeroEditButton && onHeroEditClick && (
                                        <Button
                                            onClick={onHeroEditClick}
                                            startIcon={<EditOutlined sx={{ fontSize: { xs: 15, sm: 18 } }} />}
                                            sx={heroEditPillSx}>
                                            {heroEditLabel}
                                        </Button>
                                    )}
                                </Stack>
                            )}
                        </Stack>
                    ) : isPerformerProfile && displayImage ? (
                        <Stack
                            direction="row"
                            alignItems="flex-end"
                            justifyContent="space-between"
                            spacing={{ xs: 1.25, sm: 2, md: 2.5 }}
                            sx={{
                                position: 'absolute',
                                left: 0,
                                right: 0,
                                bottom: 0,
                                zIndex: 2,
                                color: theme[themeStyle].true_white,
                                px: { xs: 2, sm: 4, md: 8 },
                                pb: { xs: 1.5 + mobileHeroActionInset / 16, md: 4 },
                                pt: { xs: 4, sm: 8 },
                                maxWidth: 'min(1280px, 100%)',
                                mx: 'auto',
                                width: '100%',
                                boxSizing: 'border-box',
                            }}>
                            <Stack
                                direction="row"
                                spacing={{ xs: 1.1, sm: 2.5, md: 3 }}
                                alignItems="flex-end"
                                sx={{ flex: 1, minWidth: 0, mt: 0 }}>
                                <Box
                                    sx={{
                                        width: { xs: 76, sm: 180, md: 224 },
                                        height: { xs: 76, sm: 180, md: 224 },
                                        borderRadius: { xs: '10px', sm: '12px' },
                                        overflow: 'hidden',
                                        borderWidth: { xs: 2, sm: 4 },
                                        borderStyle: 'solid',
                                        borderColor: theme[themeStyle].highlight,
                                        backgroundColor: '#111',
                                        flexShrink: 0,
                                        boxShadow: '0 24px 60px rgba(0,0,0,0.5)',
                                    }}>
                                    <img
                                        src={
                                            profile.profile_picture ||
                                            profile.user?.profile_picture ||
                                            displayImage ||
                                            ''
                                        }
                                        alt={displayName || 'Profile'}
                                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                    />
                                </Box>
                                <Stack spacing={{ xs: 0.75, sm: 1.5 }} sx={{ flex: 1, minWidth: 0, pb: { md: 0.5 } }}>
                                    <Typography
                                        component="h1"
                                        sx={{
                                            fontWeight: 800,
                                            fontFamily: FONT_ENUM.BOLD_HEADER,
                                            fontSize: { xs: '1.35rem', sm: '3.5rem', md: '4.25rem' },
                                            lineHeight: 1.02,
                                            letterSpacing: '-0.03em',
                                            textShadow: '0 4px 12px rgba(0,0,0,0.8)',
                                        }}>
                                        {heroNameParts.first ? (
                                            <>
                                                <Box component="span" sx={{ color: theme[themeStyle].true_white }}>
                                                    {heroNameParts.first}{' '}
                                                </Box>
                                                <Box component="span" sx={{ color: theme[themeStyle].highlight }}>
                                                    {heroNameParts.last}
                                                </Box>
                                            </>
                                        ) : (
                                            <Box component="span" sx={{ color: theme[themeStyle].highlight }}>
                                                {heroNameParts.last || displayName}
                                            </Box>
                                        )}
                                    </Typography>
                                    <Stack
                                        direction="row"
                                        alignItems="center"
                                        flexWrap="wrap"
                                        gap={{ xs: 0.75, sm: 1.5 }}>
                                        <Chip
                                            label={performerTypeLabel}
                                            size="small"
                                            sx={{
                                                height: { xs: 22, sm: 30 },
                                                fontSize: { xs: 8, sm: 11 },
                                                fontWeight: 700,
                                                letterSpacing: { xs: '0.08em', sm: '0.14em' },
                                                textTransform: 'uppercase',
                                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                                bgcolor: 'rgba(250,175,63,0.12)',
                                                color: theme[themeStyle].highlight,
                                                border: '1px solid rgba(250,175,63,0.22)',
                                                '& .MuiChip-label': { px: { xs: 0.75, sm: 1.25 } },
                                            }}
                                        />
                                        {profile.title ? (
                                            <>
                                                <Typography
                                                    sx={{
                                                        color: 'rgba(255,255,255,0.35)',
                                                        fontSize: { xs: '0.9rem', sm: '1.5rem' },
                                                        fontWeight: 300,
                                                        lineHeight: 1,
                                                    }}>
                                                    |
                                                </Typography>
                                                <Chip
                                                    label={profile.title}
                                                    size="small"
                                                    sx={{
                                                        height: { xs: 22, sm: 30 },
                                                        fontSize: { xs: 8, sm: 11 },
                                                        fontWeight: 700,
                                                        letterSpacing: { xs: '0.08em', sm: '0.12em' },
                                                        textTransform: 'uppercase',
                                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                                        bgcolor: '#262626',
                                                        color: theme[themeStyle].true_white,
                                                        border: 'none',
                                                        '& .MuiChip-label': { px: { xs: 0.75, sm: 1.25 } },
                                                    }}
                                                />
                                            </>
                                        ) : null}
                                        {!hideRating &&
                                            (hasReviewInfo ? (
                                                <Stack
                                                    direction="row"
                                                    alignItems="center"
                                                    spacing={0.5}
                                                    sx={{ ml: { xs: 0.25, sm: 0.5 } }}>
                                                    <Star
                                                        sx={{
                                                            fontSize: { xs: 14, sm: 18 },
                                                            color: theme[themeStyle].highlight,
                                                        }}
                                                    />
                                                    <Typography
                                                        sx={{
                                                            fontWeight: 700,
                                                            fontSize: { xs: 11, sm: 14 },
                                                            fontFamily: FONT_ENUM.MAIN_BOLD,
                                                        }}>
                                                        {rating}
                                                    </Typography>
                                                    <Typography
                                                        sx={{
                                                            fontSize: { xs: 10, sm: 12 },
                                                            color: 'rgba(255,255,255,0.45)',
                                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                        }}>
                                                        ({number_of_reviews} reviews)
                                                    </Typography>
                                                </Stack>
                                            ) : (
                                                <Typography
                                                    sx={{
                                                        fontSize: { xs: 10, sm: 12 },
                                                        color: 'rgba(255,255,255,0.45)',
                                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                        ml: { xs: 0.25, sm: 0.5 },
                                                    }}>
                                                    No reviews yet
                                                </Typography>
                                            ))}
                                    </Stack>
                                </Stack>
                            </Stack>
                            <Stack
                                direction={{ xs: 'column', md: 'row' }}
                                spacing={{ xs: 0.75, sm: 1.5 }}
                                alignItems="flex-end"
                                sx={{
                                    flexShrink: 0,
                                    pb: { md: 0.5 },
                                    width: { xs: 'auto', md: 'auto' },
                                    display: { xs: 'none', lg: 'flex' },
                                }}>
                                {(showHeroEditButton || showHeroSaveButton) && (
                                    <>
                                        {showHeroSaveButton && onHeroSaveClick && (
                                            <Button
                                                disabled={heroSaveDisabled}
                                                onClick={onHeroSaveClick}
                                                sx={{ ...heroSavePillSx, ...heroSaveDisabledSx }}>
                                                {heroSaveLabel}
                                            </Button>
                                        )}
                                        {showHeroEditButton && onHeroEditClick && (
                                            <Button
                                                onClick={onHeroEditClick}
                                                startIcon={<EditOutlined sx={{ fontSize: { xs: 15, sm: 18 } }} />}
                                                sx={heroEditPillSx}>
                                                {heroEditLabel}
                                            </Button>
                                        )}
                                    </>
                                )}
                            </Stack>
                        </Stack>
                    ) : (
                        <Stack
                            direction="row"
                            alignItems="flex-end"
                            justifyContent="space-between"
                            sx={{
                                position: 'absolute',
                                bottom: { xs: 18 + mobileHeroActionInset, md: 18 },
                                left: 18,
                                right: 18,
                                zIndex: 2,
                                color: theme[themeStyle].true_white,
                            }}>
                            <Stack
                                direction="row"
                                spacing={{ xs: 1.25, sm: 2 }}
                                alignItems="flex-end"
                                sx={{ minWidth: 0, flex: 1 }}>
                                <Box
                                    sx={{
                                        width: { xs: 52, sm: 84 },
                                        height: { xs: 52, sm: 84 },
                                        borderRadius: { xs: '8px', sm: '10px' },
                                        overflow: 'hidden',
                                        border: '2px solid rgba(255,255,255,0.12)',
                                        backgroundColor: '#111',
                                        flexShrink: 0,
                                    }}>
                                    <img
                                        src={
                                            profile.profile_picture ||
                                            profile.user?.profile_picture ||
                                            displayImage ||
                                            ''
                                        }
                                        alt={displayName || 'Profile'}
                                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                    />
                                </Box>
                                <Stack sx={{ pb: 0.4, minWidth: 0 }}>
                                    <Typography
                                        sx={{
                                            color: theme[themeStyle].true_white,
                                            fontWeight: 700,
                                            fontFamily: FONT_ENUM.MAIN_BOLD,
                                            fontSize: { xs: 14, sm: 22 },
                                            lineHeight: 1.1,
                                        }}>
                                        {displayName}
                                    </Typography>
                                    <Typography
                                        sx={{
                                            color: theme[themeStyle].highlight,
                                            fontWeight: 600,
                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                            fontSize: { xs: 10, sm: 13 },
                                        }}>
                                        @{profileHandle}
                                    </Typography>
                                </Stack>
                            </Stack>

                            {(showHeroEditButton || showHeroSaveButton) && (
                                <Stack
                                    direction={{ xs: 'column', md: 'row' }}
                                    spacing={{ xs: 0.75, sm: 1.25 }}
                                    alignItems="flex-end"
                                    sx={{ flexShrink: 0, display: { xs: 'none', lg: 'flex' } }}>
                                    {showHeroSaveButton && onHeroSaveClick && (
                                        <Button
                                            disabled={heroSaveDisabled}
                                            onClick={onHeroSaveClick}
                                            sx={{ ...heroSaveRoundedSx, ...heroSaveDisabledSx }}>
                                            {heroSaveLabel}
                                        </Button>
                                    )}
                                    {showHeroEditButton && onHeroEditClick && (
                                        <Button
                                            onClick={onHeroEditClick}
                                            startIcon={<EditOutlined sx={{ fontSize: { xs: 15, sm: 18 } }} />}
                                            sx={heroEditRoundedSx}>
                                            {heroEditLabel}
                                        </Button>
                                    )}
                                </Stack>
                            )}
                        </Stack>
                    )}
                    {isBelowDesktop && hasHeroActions && (
                        <Stack
                            spacing={1}
                            sx={{
                                position: 'absolute',
                                left: 16,
                                right: 16,
                                bottom: 8,
                                zIndex: 4,
                                display: { xs: 'flex', lg: 'none' },
                            }}>
                            {showHeroEditButton && onHeroEditClick && (
                                <Button
                                    onClick={onHeroEditClick}
                                    fullWidth
                                    startIcon={<EditOutlined sx={{ fontSize: 16 }} />}
                                    sx={{
                                        borderRadius: 999,
                                        py: 0.85,
                                        fontSize: '0.82rem',
                                        bgcolor: theme[themeStyle].highlight,
                                        color: theme[themeStyle].black,
                                        textTransform: 'none',
                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                        fontWeight: 700,
                                        '&:hover': {
                                            bgcolor: theme[themeStyle].primary_orange,
                                            color: theme[themeStyle].black,
                                        },
                                    }}>
                                    {heroEditLabel}
                                </Button>
                            )}
                            {showHeroSaveButton && onHeroSaveClick && (
                                <Button
                                    disabled={heroSaveDisabled}
                                    onClick={onHeroSaveClick}
                                    fullWidth
                                    sx={{
                                        borderRadius: 999,
                                        py: 0.85,
                                        fontSize: '0.82rem',
                                        bgcolor: theme[themeStyle].highlight,
                                        color: theme[themeStyle].black,
                                        textTransform: 'none',
                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                        fontWeight: 700,
                                        '&:hover': {
                                            bgcolor: theme[themeStyle].primary_orange,
                                            color: theme[themeStyle].black,
                                        },
                                        ...heroSaveDisabledSx,
                                    }}>
                                    {heroSaveLabel}
                                </Button>
                            )}
                        </Stack>
                    )}

                    {/* Rating Circle Overlay */}
                    {!hideRating && hasReviewInfo && !usesArtistStyleHero && !(isPerformerProfile && displayImage) && (
                        <Box
                            sx={{
                                position: 'absolute',
                                right: 16,
                                bottom: isBelowDesktop && hasHeroActions ? 106 : 16,
                                width: isMobile ? 70 : 100,
                                height: isMobile ? 70 : 100,
                                borderRadius: '50%',
                                background: theme[themeStyle].secondaryBg,
                                boxShadow: 3,
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                zIndex: 2,
                                border: `3px solid ${theme[themeStyle].highlight}`,
                            }}>
                            <StarRating
                                defaultValue={Number(rating)}
                                ratingProps={{
                                    readOnly: true,
                                }}
                                style={{
                                    width: 'fit-content',
                                    height: 'fit-content',
                                }}
                                iconSize={10}
                            />
                            <Typography
                                sx={{
                                    color: theme[themeStyle].tag_white,
                                    fontWeight: 700,
                                    fontSize: isMobile ? 16 : 20,
                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                }}>
                                {rating}
                            </Typography>
                            <Typography
                                sx={{
                                    color: theme[themeStyle].grey,
                                    fontSize: isMobile ? 8 : 10,
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    mt: -0.5,
                                }}>
                                {number_of_reviews} reviews
                            </Typography>
                        </Box>
                    )}

                    {/* Edit Icon Overlay */}
                    {editMode && !pendingImage && (
                        <IconButton
                            sx={{
                                position: 'absolute',
                                top: 12,
                                right: 12,
                                backgroundColor: 'rgba(0,0,0,0.5)',
                                color: theme[themeStyle].primary_white,
                                zIndex: 10,
                                '&:hover': { backgroundColor: theme[themeStyle].primary_orange },
                            }}
                            onClick={handleUploadClick}
                            aria-label="Upload Profile Image">
                            <PhotoCamera sx={{ color: theme[themeStyle].true_white }} />
                            <input
                                type="file"
                                accept="image/*"
                                ref={fileInputRef}
                                style={{ display: 'none' }}
                                onChange={handleFileChange}
                            />
                        </IconButton>
                    )}

                    {/* Cropper Modal */}
                    <RectangularImageCropperModal
                        open={modalOpen}
                        src={src}
                        onSave={handleCropSave}
                        onCancel={handleCropCancel}
                        aspect={16 / 5}
                        cropHeight={imageHeight}
                    />

                    {/* Pending image preview overlay with Save/Cancel */}
                    {editMode && pendingImage && (
                        <Box
                            sx={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                height: '100%',
                                zIndex: 20,
                                background: 'rgba(0,0,0,0.25)',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}>
                            <img
                                src={pendingImage}
                                alt="Preview"
                                style={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover',
                                    borderRadius: 8,
                                    boxShadow: '0 2px 16px rgba(0,0,0,0.2)',
                                }}
                            />
                            <Box sx={{ position: 'absolute', top: 20, right: 20, display: 'flex', gap: 1.5 }}>
                                <Button variant="contained" onClick={handlePendingCancel} sx={heroSavePillSx}>
                                    Cancel
                                </Button>
                                <Button variant="contained" onClick={handlePendingSave} sx={heroSavePillSx}>
                                    Save
                                </Button>
                            </Box>
                        </Box>
                    )}
                </Box>
            </Box>
        </Box>
    );
};

export default WorkProfileHeader;
